import { Capacitor } from '@capacitor/core';
import { Downloads } from '@/plugins/downloads';

export interface DownloadMediaOptions {
  url: string;
  fileName?: string;
  mimeType?: string;
}

export interface DownloadMediaResult {
  success: boolean;
  platform: 'native' | 'web';
  fileName: string;
  uri?: string;
  error?: unknown;
}

/**
 * Derive a saved file name from a URL.
 *
 * Parses the URL safely, drops query/hash, takes the last path segment, decodes
 * it, strips path separators, and trims. Returns `fallback` when the result is
 * blank. Never forces or invents an extension.
 */
export function deriveFileNameFromUrl(url: string, fallback = 'download'): string {
  try {
    // Resolve against the current location so relative URLs parse too.
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    // `pathname` already excludes query (`search`) and hash.
    const lastSegment = parsed.pathname.split('/').pop() ?? '';

    let decoded: string;
    try {
      decoded = decodeURIComponent(lastSegment);
    } catch {
      // Malformed percent-encoding — fall back to the raw segment.
      decoded = lastSegment;
    }

    const cleaned = decoded.replace(/[/\\]/g, '').trim();
    return cleaned.length > 0 ? cleaned : fallback;
  } catch {
    return fallback;
  }
}

/** Resolve the final file name: trimmed option -> URL-derived -> 'download'. */
function resolveFileName(options: DownloadMediaOptions): string {
  const explicit = options.fileName?.trim();
  if (explicit) return explicit;
  return deriveFileNameFromUrl(options.url, 'download');
}

/**
 * Download a media file to the device.
 *
 * On a Capacitor native platform the custom `Downloads` plugin saves to the
 * public Android Downloads collection. On web the file is fetched, turned into a
 * Blob, and saved via a temporary anchor.
 *
 * Never rejects — inspect `result.success`.
 */
export async function downloadMedia(options: DownloadMediaOptions): Promise<DownloadMediaResult> {
  const fileName = resolveFileName(options);

  if (Capacitor.isNativePlatform()) {
    const mimeType = options.mimeType?.trim() || 'application/octet-stream';
    try {
      const result = await Downloads.downloadToDownloads({
        url: options.url,
        fileName,
        mimeType,
      });
      return {
        success: result.success,
        platform: 'native',
        fileName: result.fileName ?? fileName,
        uri: result.uri,
        error: result.success ? undefined : new Error('Native download reported failure'),
      };
    } catch (error) {
      return { success: false, platform: 'native', fileName, error };
    }
  }

  // Web path.
  try {
    const response = await fetch(options.url, { mode: 'cors', cache: 'no-cache' });
    if (!response.ok) {
      return {
        success: false,
        platform: 'web',
        fileName,
        error: new Error(`Download request failed with status ${response.status}`),
      };
    }

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);

    return { success: true, platform: 'web', fileName };
  } catch (error) {
    return { success: false, platform: 'web', fileName, error };
  }
}